import { Head, Link, router } from "@inertiajs/react"
import { ArrowDown, ArrowUp, ArrowUpDown, Briefcase, Plus } from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { AgencyListActAsButton } from "@/components/admin/agency-list-act-as-button"
import { ListPagination } from "@/components/admin/list-pagination"
import { ListShell } from "@/components/admin/list-shell"
import { ListToolbar } from "@/components/admin/list-toolbar"
import { NewTenantDialog } from "@/components/admin/new-tenant-dialog"
import { EmptyState } from "@/components/empty-state"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { nameOrDash } from "@/lib/admin-placeholder"
import { adminAgenciesPath, adminAgencyPath } from "@/routes"
import type { AgenciesIndexProps } from "@/types/admin"

type SortableColumn = "name" | "created_at"

/**
 * Admin/Agencies/Index — paginated agency list (OKS-15 AC3).
 *
 * Same shape as `Admin/Brands/Index` but different columns + no agency
 * mutability. Backend's `Admin::Agencies#index` ships
 * `agencies/pagination/filters/sort`. The Brands column for "agency"
 * here becomes "Brands" — Backend serialises a count + the first 3
 * brand names (`brand_preview`) so we can render a compact summary
 * without a second round trip per row.
 */
export default function AdminAgenciesIndex({
  agencies,
  pagination,
  filters,
  sort,
  can_create_agency,
}: AgenciesIndexProps) {
  const { t, i18n } = useTranslation()

  const hasActiveFilters =
    filters.q.length > 0 ||
    filters.created_from.length > 0 ||
    filters.created_to.length > 0
  const isEmpty = agencies.length === 0

  const visit = useCallback(
    (
      overrides: Partial<{
        q: string
        page: number
        sort_by: string
        sort_dir: string
      }>,
    ) => {
      const clean: Record<string, string | number> = {}
      const merged: Record<string, string | number> = {
        q: filters.q,
        created_from: filters.created_from,
        created_to: filters.created_to,
        sort_by: sort.by,
        sort_dir: sort.dir,
        page: pagination.page,
        ...overrides,
      }
      for (const [k, v] of Object.entries(merged)) {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v
      }
      router.get(adminAgenciesPath(), clean, {
        preserveState: true,
        preserveScroll: true,
        only: ["agencies", "pagination", "filters", "sort"],
      })
    },
    [
      filters.q,
      filters.created_from,
      filters.created_to,
      sort.by,
      sort.dir,
      pagination.page,
    ],
  )

  const onSearch = useCallback((q: string) => visit({ q, page: 1 }), [visit])
  const onPageChange = useCallback((page: number) => visit({ page }), [visit])
  const onSortToggle = useCallback(
    (column: SortableColumn) => {
      const nextDir = sort.by === column && sort.dir === "desc" ? "asc" : "desc"
      visit({ sort_by: column, sort_dir: nextDir, page: 1 })
    },
    [sort.by, sort.dir, visit],
  )
  const onClearFilters = useCallback(() => {
    router.get(
      adminAgenciesPath(),
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["agencies", "pagination", "filters", "sort"],
      },
    )
  }, [])

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  return (
    <AppLayout>
      <Head title={t("admin.agencies_list.title")} />
      <ListShell
        testId="admin-agencies-list"
        title={t("admin.agencies_list.title")}
        subtitle={t("admin.agencies_list.subtitle_total", {
          count: pagination.total,
        })}
        action={
          can_create_agency ? (
            <NewTenantDialog
              kind="agency"
              trigger={
                <Button type="button" data-testid="admin-new-agency-button">
                  <Plus className="size-4" aria-hidden="true" />
                  {t("admin.invites.dialog.new_agency.trigger")}
                </Button>
              }
            />
          ) : undefined
        }
        toolbar={
          <ListToolbar searchValue={filters.q} onSearchChange={onSearch} />
        }
        footer={
          <ListPagination
            page={pagination.page}
            perPage={pagination.per_page}
            total={pagination.total}
            onPageChange={onPageChange}
          />
        }
      >
        {isEmpty ? (
          hasActiveFilters ? (
            <EmptyState
              data-testid="admin-list-empty"
              title={t("admin.list.empty_filter_agencies")}
              actions={
                <Button
                  variant="outline"
                  onClick={onClearFilters}
                  data-testid="admin-list-empty-clear"
                >
                  {t("admin.shared.clear_filters")}
                </Button>
              }
            />
          ) : (
            <EmptyState
              data-testid="admin-list-empty"
              icon={Briefcase}
              title={t("admin.list.empty_fresh_agencies")}
            />
          )
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortHeader
                    column="name"
                    label={t("admin.agencies_list.column.name")}
                    sort={sort}
                    onToggle={onSortToggle}
                  />
                </TableHead>
                <TableHead>
                  <SortHeader
                    column="created_at"
                    label={t("admin.agencies_list.column.created_at")}
                    sort={sort}
                    onToggle={onSortToggle}
                  />
                </TableHead>
                <TableHead className="text-right">
                  {t("admin.agencies_list.column.team_size")}
                </TableHead>
                <TableHead>{t("admin.agencies_list.column.brands")}</TableHead>
                <TableHead>
                  <span className="sr-only">
                    {t("admin.agencies_list.column.view")}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agencies.map((agency) => (
                <TableRow
                  key={agency.id}
                  data-testid="admin-list-row"
                  data-agency-id={agency.id}
                >
                  <TableCell className="font-medium">{agency.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {dateFmt.format(new Date(agency.created_at))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {agency.team_size}
                  </TableCell>
                  <TableCell>
                    <BrandsSummary
                      count={agency.brands_count}
                      preview={agency.brand_preview}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {/*
                     * OKS-683 — Per-row "Act as {Agency}" entry, before the
                     * "View" link (mirrors the brand list). The whole
                     * `/admin/agencies` surface is admin-gated, and the
                     * server re-authorises via `Admin::ActAsAgencyPolicy`, so
                     * the button renders unconditionally (no per-row flag).
                     */}
                    <div className="flex items-center justify-end gap-2">
                      <AgencyListActAsButton
                        agency={{ id: agency.id, name: agency.name }}
                      />
                      <Link
                        href={adminAgencyPath({ id: agency.id })}
                        className="text-foreground hover:text-foreground/80 text-sm font-medium underline-offset-4 hover:underline"
                        aria-label={t("admin.agencies_list.view_label", {
                          name: agency.name,
                        })}
                      >
                        {t("admin.agencies_list.column.view")}
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListShell>
    </AppLayout>
  )
}

interface BrandsSummaryProps {
  count: number
  // OKS-905 — `name: string | null`; guarded orphaned brand renders "—".
  preview: { id: number; name: string | null }[]
}

function BrandsSummary({ count, preview }: BrandsSummaryProps) {
  const { t } = useTranslation()
  if (count === 0) {
    return (
      <span className="text-muted-foreground text-sm">
        {t("admin.agencies_list.brands_summary.none")}
      </span>
    )
  }
  const remaining = count - preview.length
  return (
    <div className="flex flex-wrap items-center gap-1">
      {preview.map((b) => (
        <Badge key={b.id} variant="secondary" className="font-normal">
          {nameOrDash(b.name)}
        </Badge>
      ))}
      {remaining > 0 && (
        <span className="text-muted-foreground text-xs">
          {t("admin.agencies_list.brands_summary.with_more", {
            count: remaining,
          })}
        </span>
      )}
    </div>
  )
}

interface SortHeaderProps {
  column: SortableColumn
  label: string
  sort: { by: string; dir: "asc" | "desc" }
  onToggle: (column: SortableColumn) => void
}

function SortHeader({ column, label, sort, onToggle }: SortHeaderProps) {
  const { t } = useTranslation()
  const isActive = sort.by === column
  const Icon = isActive
    ? sort.dir === "asc"
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown
  return (
    <button
      type="button"
      onClick={() => onToggle(column)}
      className="text-foreground hover:text-foreground/80 inline-flex items-center gap-1 text-xs font-medium tracking-wide uppercase"
      aria-label={t("admin.brands_list.sort.toggle_label", { column: label })}
      aria-sort={
        isActive ? (sort.dir === "asc" ? "ascending" : "descending") : "none"
      }
      data-testid={`admin-list-sort-${column}`}
    >
      <span>{label}</span>
      <Icon className="size-3.5" aria-hidden="true" />
    </button>
  )
}
